/** Ads-scoped org prefs stored under `organizations.sales_daily_checklist_config.ads`. */

export const ADS_FEED_RETENTION_MAX_DAYS = 60;
export const ADS_FEED_RETENTION_DEFAULT_DAYS = 60;
export const ADS_MIN_EXEMPLARS_DEFAULT = 5;
export const ADS_MIN_EXEMPLARS_FLOOR = 3;

export const ADS_ACTIVITY_RED_AFTER_LOCAL_TIME_DEFAULT = "15:00";
export const ADS_ACTIVITY_DAILY_MAX_PER_PLATFORM_DEFAULT = 3;
export const ADS_ACTIVITY_DAILY_MAX_PER_PLATFORM_MIN = 1;
export const ADS_ACTIVITY_DAILY_MAX_PER_PLATFORM_MAX = 20;

const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export type AdsAssistantOrgPrefs = {
  feed_retention_days: number;
  show_recent_logger_names: boolean;
  suggestion_stats_scope_default: "org" | "location";
  min_exemplars_for_template_signoff: number;
  archive_export: { enabled: boolean; destination_hint?: string };
  /** Gamification Phase 1: when false, the org's checklist ad goal is not used for the Org Activity verdict. */
  use_in_org_activity: boolean;
  /** Local HH:MM cutoff after which an unmet daily goal flips amber → red. */
  red_after_local_time: string;
  /** Soft cap per platform per day; multiplied by `platform_entries.length` for the daily soft cap. */
  daily_max_per_platform: number;
  /** Slice 18d: nightly CRM Social Planner pull (cron). */
  ghl_auto_sync_enabled: boolean;
  ghl_auto_sync_days_back: number;
  ghl_auto_sync_limit: number;
};

export const ADS_GHL_AUTO_SYNC_DAYS_BACK_DEFAULT = 7;
export const ADS_GHL_AUTO_SYNC_LIMIT_DEFAULT = 50;

function clampInt(n: number, lo: number, hi: number): number {
  if (!Number.isFinite(n)) return lo;
  return Math.min(hi, Math.max(lo, Math.round(n)));
}

export function parseAdsAssistantOrgPrefsFromChecklistConfig(raw: unknown): AdsAssistantOrgPrefs {
  let ads: Record<string, unknown> = {};
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const a = (raw as Record<string, unknown>).ads;
    if (a && typeof a === "object" && !Array.isArray(a)) ads = a as Record<string, unknown>;
  }

  const fr = ads.feed_retention_days;
  let feed_retention_days =
    typeof fr === "number" && Number.isFinite(fr) ? Math.round(fr) : ADS_FEED_RETENTION_DEFAULT_DAYS;
  feed_retention_days = clampInt(feed_retention_days, 1, ADS_FEED_RETENTION_MAX_DAYS);

  const show_recent_logger_names = ads.show_recent_logger_names === true;

  const scopeRaw = ads.suggestion_stats_scope_default;
  const suggestion_stats_scope_default =
    scopeRaw === "location" ? "location" : "org";

  const me = ads.min_exemplars_for_template_signoff;
  let min_exemplars_for_template_signoff =
    typeof me === "number" && Number.isFinite(me) ? Math.round(me) : ADS_MIN_EXEMPLARS_DEFAULT;
  min_exemplars_for_template_signoff = clampInt(
    min_exemplars_for_template_signoff,
    ADS_MIN_EXEMPLARS_FLOOR,
    50,
  );

  const arch = ads.archive_export;
  let archive_export: AdsAssistantOrgPrefs["archive_export"] = { enabled: false };
  if (arch && typeof arch === "object" && !Array.isArray(arch)) {
    const o = arch as Record<string, unknown>;
    archive_export = {
      enabled: o.enabled === true,
      ...(typeof o.destination_hint === "string" && o.destination_hint.trim()
        ? { destination_hint: o.destination_hint.trim().slice(0, 2000) }
        : {}),
    };
  }

  const use_in_org_activity = ads.use_in_org_activity !== false;

  const rRaw = ads.red_after_local_time;
  const red_after_local_time =
    typeof rRaw === "string" && HHMM_RE.test(rRaw.trim())
      ? rRaw.trim()
      : ADS_ACTIVITY_RED_AFTER_LOCAL_TIME_DEFAULT;

  const dRaw = ads.daily_max_per_platform;
  let daily_max_per_platform =
    typeof dRaw === "number" && Number.isFinite(dRaw)
      ? Math.round(dRaw)
      : ADS_ACTIVITY_DAILY_MAX_PER_PLATFORM_DEFAULT;
  daily_max_per_platform = clampInt(
    daily_max_per_platform,
    ADS_ACTIVITY_DAILY_MAX_PER_PLATFORM_MIN,
    ADS_ACTIVITY_DAILY_MAX_PER_PLATFORM_MAX,
  );

  const ghl_auto_sync_enabled = ads.ghl_auto_sync_enabled === true;

  const ghlDaysRaw = ads.ghl_auto_sync_days_back;
  let ghl_auto_sync_days_back =
    typeof ghlDaysRaw === "number" && Number.isFinite(ghlDaysRaw)
      ? Math.round(ghlDaysRaw)
      : ADS_GHL_AUTO_SYNC_DAYS_BACK_DEFAULT;
  ghl_auto_sync_days_back = clampInt(ghl_auto_sync_days_back, 1, 90);

  const ghlLimitRaw = ads.ghl_auto_sync_limit;
  let ghl_auto_sync_limit =
    typeof ghlLimitRaw === "number" && Number.isFinite(ghlLimitRaw)
      ? Math.round(ghlLimitRaw)
      : ADS_GHL_AUTO_SYNC_LIMIT_DEFAULT;
  ghl_auto_sync_limit = clampInt(ghl_auto_sync_limit, 1, 100);

  return {
    feed_retention_days,
    show_recent_logger_names,
    suggestion_stats_scope_default,
    min_exemplars_for_template_signoff,
    archive_export,
    use_in_org_activity,
    red_after_local_time,
    daily_max_per_platform,
    ghl_auto_sync_enabled,
    ghl_auto_sync_days_back,
    ghl_auto_sync_limit,
  };
}

/** ISO timestamp: posts with `published_at` strictly before this are outside the hot feed window. */
export function adsFeedRetentionCutoffIso(prefs: AdsAssistantOrgPrefs): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - prefs.feed_retention_days);
  return d.toISOString();
}
